import { z } from "zod";

/**
 * LES MENTIONS D'UN MESSAGE : la convention de texte STOCKE, et ce qu'on en
 * derive (contrat conversations du 05/10/2026, section 3).
 *
 *   - une personne : `@[personId]`   (jamais un nom : un nom change)
 *   - tout le monde : `@tous`
 *   - Lia : `@Lia`  (insensible a la casse a la lecture)
 *
 * CE FICHIER EST ECRIT DEUX FOIS, et c'est assume : ici (le SDK derive
 * `mentions` du texte a l'envoi, les Functions le lisent) et dans
 * `packages/ui/src/conversations/mentions.ts` (`@taki/ui` ne depend d'aucun
 * modele de donnees). Le test `packages/ui/tests/conversations-mentions.test.mjs`
 * compare les deux sur les memes textes.
 */

export const MENTION_TOUS = "@tous";
export const MENTION_LIA = "@Lia";

/** Une mention stockee : `@[personId]`, `@tous`, `@Lia`. Frontiere : pas de lettre ou de chiffre colle apres. */
export const MOTIF_MENTION_STOCKEE = /@\[([A-Za-z0-9_-]{1,64})\]|@(tous|lia)(?![\p{L}\p{N}_])/giu;

export const mentionsSchema = z
  .object({
    personnes: z.array(z.string().min(1)).max(100),
    tous: z.boolean(),
    lia: z.boolean(),
  })
  .strict();
export type Mentions = z.infer<typeof mentionsSchema>;

export const MENTIONS_VIDES: Mentions = Object.freeze({ personnes: [], tous: false, lia: false }) as Mentions;

/** Les mentions d'un texte stocke : personIds sans doublon dans l'ordre d'apparition, `tous`, `lia`. */
export function mentionsDe(texte: string): Mentions {
  const personnes: string[] = [];
  let tous = false;
  let lia = false;
  for (const trouve of texte.matchAll(MOTIF_MENTION_STOCKEE)) {
    const personId = trouve[1];
    if (personId !== undefined) {
      if (!personnes.includes(personId)) personnes.push(personId);
    } else if (trouve[2]?.toLowerCase() === "tous") {
      tous = true;
    } else {
      lia = true;
    }
  }
  return { personnes, tous, lia };
}

/** Le jeton stocke d'une personne. */
export function jetonDePersonne(personId: string): string {
  return `@[${personId}]`;
}

/** Fusionne ce que l'editeur sait avec ce que le texte dit : l'union, sans doublon. */
export function fusionnerMentions(a: Mentions, b: Partial<Mentions> | undefined): Mentions {
  if (b === undefined) return a;
  return {
    personnes: [...new Set([...a.personnes, ...(b.personnes ?? [])])],
    tous: a.tous || b.tous === true,
    lia: a.lia || b.lia === true,
  };
}

/** Le texte stocke, les jetons remplaces par des noms (pour un modele, un mail, un extrait). */
export function texteAvecNoms(texte: string, nomDe: (personId: string) => string | undefined): string {
  return texte.replace(MOTIF_MENTION_STOCKEE, (jeton, personId: string | undefined, mot: string | undefined) => {
    if (personId !== undefined) return `@${nomDe(personId) ?? "Compte inconnu"}`;
    return mot?.toLowerCase() === "tous" ? MENTION_TOUS : MENTION_LIA;
  });
}

/**
 * L'annuaire d'un resume : une fonction, une `Map` personId -> nom, ou une
 * liste d'entrees telle que l'ecran l'a deja (`{ id, libelle }` de `@taki/ui`,
 * `{ personId, displayName }` de `sdk.personnes`).
 */
export type AnnuaireDeMentions =
  | ((personId: string) => string | undefined)
  | ReadonlyMap<string, string>
  | ReadonlyArray<{ id: string; libelle: string } | { personId: string; displayName: string }>;

function nomDansLAnnuaire(annuaire: AnnuaireDeMentions): (personId: string) => string | undefined {
  if (typeof annuaire === "function") return annuaire;
  if (annuaire instanceof Map) return (personId) => annuaire.get(personId);
  const noms = new Map<string, string>();
  for (const entree of annuaire as ReadonlyArray<Record<string, unknown>>) {
    const id = typeof entree["personId"] === "string" ? entree["personId"] : entree["id"];
    const nom = typeof entree["displayName"] === "string" ? entree["displayName"] : entree["libelle"];
    if (typeof id === "string" && typeof nom === "string" && nom.trim() !== "" && !noms.has(id)) noms.set(id, nom.trim());
  }
  return (personId) => noms.get(personId);
}

/**
 * LE RESUME TEXTE D'UN MESSAGE (verification independante du 06/10, ecart J) :
 * partout ou un message devient du TEXTE (titre de fil, apercu de liste,
 * resultat de recherche, notification, journal, outil de Lia), les mentions
 * sont rendues en noms (`@Marc`, `@tous`, `@Lia`, `@Compte inconnu`), jamais
 * `@[id]`. Sans `max` : le texte entier, retours a la ligne gardes. Avec
 * `max` : une ligne, coupee a `max` caracteres (`...` compris). Recopiee a
 * l'identique cote Functions (`functions/src/conversations/contrat.ts`).
 */
export function resumeDeMessage(texte: string, annuaire: AnnuaireDeMentions, max?: number): string {
  const rendu = texteAvecNoms(texte, nomDansLAnnuaire(annuaire));
  if (max === undefined) return rendu;
  const ligne = rendu.replace(/\s+/g, " ").trim();
  return ligne.length > max ? `${ligne.slice(0, Math.max(0, max - 3))}...` : ligne;
}

/** Le texte sans la mention de Lia (ce qu'on lui envoie), espaces normalises. */
export function sansMentionLia(texte: string): string {
  return texte.replace(/@lia(?![\p{L}\p{N}_])/giu, "").replace(/[ \t]{2,}/g, " ").trim();
}
